import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isRetrying: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    isRetrying: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleRetry = async () => {
    this.setState({ isRetrying: true });
    try {
      // Clear any cached data
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.clear();
      
      // Wait briefly
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Reload the page
      window.location.reload();
    } catch (err) {
      console.error('Error during retry:', err);
      this.setState({ isRetrying: false });
    }
  };
  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#1A1B1E] p-4">
          <div className="bg-[#2A2B2E] rounded-3xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold">Something went wrong</h2>
            </div>
            
            <p className="text-gray-400 mb-6">
              {this.state.error instanceof Error && this.state.error.message.includes('Failed to fetch')
                ? 'Connection error. Please check your internet connection and try again.'
                : 'An error occurred while loading this page. Please try refreshing or contact support if the problem persists.'}
            </p>

            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                disabled={this.state.isRetrying}
                className="w-full bg-[var(--accent-color)] text-black rounded-xl py-3 font-medium hover:opacity-90 transition-opacity"
              >
                <div className="flex items-center justify-center gap-2">
                  <RefreshCw className={`w-5 h-5 ${this.state.isRetrying ? 'animate-spin' : ''}`} />
                  {this.state.isRetrying ? 'Retrying...' : 'Retry'}
                </div>
              </button>
              
              <button
                onClick={() => window.location.href = '/'}
                className="w-full bg-black/20 text-white rounded-xl py-3 font-medium hover:bg-black/30 transition-colors"
              >
                <div className="flex items-center justify-center gap-2">
                  <Home className="w-5 h-5" />
                  Return Home
                </div>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}